import { EnvelopeStatusBadge } from '@/components/docusign/EnvelopeStatusBadge';
import { Skeleton } from '@/components/ui';
import type { SignatureState } from '@/lib/outstanding-envelopes';

/**
 * The badge for one row, or an honest absence.
 *
 * Shared by the table cell and the mobile card so the two cannot word the same
 * envelope differently — the same reason `EnvelopeStatusBadge` itself exists.
 *
 * `statusCheckedAt` is threaded through because there is **no webhook**: a
 * stored status only moves when something calls `/refresh`, so the badge dates
 * its own staleness. Nothing here is worded as live tracking.
 *
 * ⚠️ **"Something", not "somebody" — corrected 2026-08-30.** A backend worker
 * calls that route on a timer for outstanding envelopes, so a badge here can
 * change with nobody having pressed anything. It does not make the column live:
 * the pass is periodic, backs off after a failure, and does not run where
 * DocuSign is unconfigured. `BACKGROUND_CHECK_NOTE` in `@/lib/docusign` is the
 * one copy of that claim.
 *
 * ⚠️ `EnvelopeStatusBadge`'s `connection` prop is deliberately NOT passed, and
 * omitting it resolves through `signaturesNotBinding()` to *warn*, never to
 * reassure — see the block above `OUTSTANDING_PER_PAGE` in
 * `@/lib/outstanding-envelopes` for why the connection is not fetched.
 */
export function SignatureCell({ state, quoteId }: { state: SignatureState; quoteId: number }) {
  if (state.kind === 'loading') return <Skeleton className="h-4 w-24" />;
  // The backend has no DocuSign half mounted. Not a failure, and not a state to
  // explain once per row — the column simply has nothing to say.
  if (state.kind === 'unavailable') return null;

  if (state.kind === 'error') {
    return (
      <span className="text-xs text-fg-subtle" title={`Signature state couldn’t be loaded. ${state.message}`}>
        Unknown
      </span>
    );
  }

  const envelope = state.byQuoteId.get(quoteId);
  if (!envelope) {
    return (
      <span className="text-xs text-fg-subtle">
        <span aria-hidden="true">—</span>
        <span className="sr-only">Not awaiting signature</span>
      </span>
    );
  }

  return (
    <EnvelopeStatusBadge
      status={envelope.status}
      terminal={envelope.terminal}
      statusCheckedAt={envelope.statusCheckedAt}
    />
  );
}
